import React from 'react'
import PropTypes from 'prop-types'

import {
  ClipboardIcon,
  UserGroupIcon,
  EmojiHappyIcon,
  LightningBoltIcon,
  CreditCardIcon,
  CubeTransparentIcon
} from '@heroicons/react/outline'

const registrationFeatures = [
  {
    id: 1,
    name: 'Automatic rostering',
    description:
      'Spending hours creating rosters? Fixing errors parents make on registration? Never again. We roster students into the right class automatically, no matter where they register.',
    icon: CubeTransparentIcon
  },
  {
    id: 2,
    name: 'Process registration payments',
    description:
      'Parents register and pay for classes online, so you can skip manual payment logs. For Learn to Skate USA programs, parents manage membership registration and renewal directly with LTS USA and enter their membership number in Leevo.',
    icon: CreditCardIcon
  },
  {
    id: 3,
    name: 'Register through existing software',
    description:
      'Already have your own registration system? No problem. Leevo can pull data from systems such as DASH, AthleteTrax, RecPro and RecTrac. Parents register there, and Leevo handles the rest.',
    icon: LightningBoltIcon
  }
]
const coachingFeatures = [
  {
    id: 1,
    name: 'Ditch clipboards and paper',
    description:
      'Coaches track attendance and skills in seconds, with just a few taps. Leevo makes it so simple that 95%+ of coaches enter their data every class.',
    icon: ClipboardIcon
  },
  {
    id: 2,
    name: 'Coaching Recommendations',
    description:
      'Coaches (including substitutes!) are provided with recommended skills to teach in each class, ensuring that students progress and keep coming back.',
    icon: UserGroupIcon
  },
  {
    id: 3,
    name: 'Automated Progress Reporting',
    description:
      "Keep your parents informed with automated notifications on student progress. Over 80% of parents check student progress every week. Engage them in a way that's never been possible before.",
    icon: EmojiHappyIcon
  }
]

function MainFeaturesSection() {
  return (
    <div className="py-16 bg-gray-50 overflow-hidden lg:py-24 dark:bg-gray-700">
      <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
        <svg
          className="hidden lg:block absolute left-full transform -translate-x-1/2 -translate-y-1/4"
          width={404}
          height={784}
          fill="none"
          viewBox="0 0 404 784"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect x={0} y={0} width={4} height={4} className="text-gray-200 dark:text-gray-800" fill="currentColor" />
            </pattern>
          </defs>
          <rect
            width={404}
            height={784}
            className="text-gray-200 dark:text-gray-800"
            fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)"
          />
        </svg>

        <div className="relative">
          <h2 className="text-center text-3xl leading-8 font-extrabold tracking-tight text-gray-900 dark:text-gray-300 sm:text-4xl">
            A better way to run your program
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-center text-xl text-gray-500 dark:text-gray-400">
            Delight parents and coaches. Exceed expectations, whether you have 10 students or thousands.
          </p>
        </div>

        <div className="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
          <div className="relative">
            <h3 className="text-2xl font-extrabold text-gray-900 dark:text-gray-300 tracking-tight sm:text-3xl">
              Give your coaches super powers
            </h3>
            <p className="mt-3 text-lg text-gray-500 dark:text-gray-400">
              It&apos;s never been easier engage parents and kids. Give your coaches confidence and make their job 10x
              easier.
            </p>
            <dl className="mt-10 space-y-10">
              {coachingFeatures.map((item) => (
                <div key={item.id} className="relative">
                  <dt>
                    <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-purple-700 text-white dark:text-gray-300">
                      <item.icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900 dark:text-gray-300">{item.name}</p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-400">{item.description}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-10 -mx-4 relative lg:mt-0" aria-hidden="true">
            <svg
              className="absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden"
              width={784}
              height={404}
              fill="none"
              viewBox="0 0 784 404"
            >
              <defs>
                <pattern
                  id="ca9667ae-9f92-4be7-abcb-9e3d727f2941"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200 dark:text-gray-800"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect width={784} height={404} fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)" />
            </svg>
            <img
              className="relative mx-auto w-[320px] md:w-[420px]"
              src="https://leevo-public.s3.amazonaws.com/images/coaching-screenshot.png"
              alt="Registration Screenshot"
            />
          </div>
        </div>

        <svg
          className="hidden lg:block absolute right-full transform translate-x-1/2 translate-y-12"
          width={404}
          height={784}
          fill="none"
          viewBox="0 0 404 784"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect x={0} y={0} width={4} height={4} className="text-gray-200 dark:text-gray-800" fill="currentColor" />
            </pattern>
          </defs>
          <rect width={404} height={784} fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)" />
        </svg>

        <div className="relative mt-12 sm:mt-16 lg:mt-24">
          <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div className="lg:col-start-2">
              <h3 className="text-2xl font-extrabold text-gray-900 dark:text-gray-300 tracking-tight sm:text-3xl">
                Simple Rostering. Period.
              </h3>
              <p className="mt-3 text-lg text-gray-500 dark:text-gray-400">
                Handling rosters shouldn&apos;t take you 20 hours a week. Stop juggling registration softwares,
                websites, forms and spreadsheets. Let us handle it.
              </p>

              <dl className="mt-10 space-y-10">
                {registrationFeatures.map((item) => (
                  <div key={item.id} className="relative">
                    <dt>
                      <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-purple-700 text-white dark:text-gray-300">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg leading-6 font-medium text-gray-900 dark:text-gray-300">
                        {item.name}
                      </p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-400">{item.description}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-10 -mx-4 relative lg:mt-0 lg:col-start-1">
              <svg
                className="absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden"
                width={784}
                height={404}
                fill="none"
                viewBox="0 0 784 404"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="e80155a9-dfde-425a-b5ea-1f6fadd20131"
                    x={0}
                    y={0}
                    width={20}
                    height={20}
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x={0}
                      y={0}
                      width={4}
                      height={4}
                      className="text-gray-200 dark:text-gray-800"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect width={784} height={404} fill="url(#e80155a9-dfde-425a-b5ea-1f6fadd20131)" />
              </svg>
              <img
                className="relative mx-auto w-[320px] md:w-[420px]"
                src="https://leevo-public.s3.amazonaws.com/images/registration-screenshot.png"
                alt="Coaching screenshot"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

MainFeaturesSection.propTypes = {
  className: PropTypes.string
}

export default MainFeaturesSection
